import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  MessageSquare,
  ThumbsUp,
  Users,
  Loader2,
  AlertTriangle,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/shared/components/ui/Dialog';
import { Badge } from '@/shared/components/ui/Badge';
import { UserSelectorAvatar } from '@/shared/components/user-selector/UserSelectorAvatar';
import { useInteractionsByIds } from '@/shared/hooks/useInteractions';
import { InteractionType, FeedbackStatus } from '@momentum/types';
import type { PopulatedInteraction } from '@momentum/types';
import { cn } from '@/shared/lib/utils';

interface GoalInteractionsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  interactionIds: string[];
  goalTitle: string;
  includeInteractionTypes?: InteractionType[];
}

export const GoalInteractionsDialog: React.FC<GoalInteractionsDialogProps> = ({
  open,
  onOpenChange,
  interactionIds,
  goalTitle,
  includeInteractionTypes,
}) => {
  const { t, i18n } = useTranslation('goals');
  const { data: interactions = [], isLoading } = useInteractionsByIds(
    interactionIds,
    open
  );

  const interactionConfig = {
    [InteractionType.PRAISE]: {
      icon: ThumbsUp,
      label: t('interactions.interactionTypeISeeYou'),
      colorClass:
        'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-50',
      iconColor: 'text-emerald-600',
    },
    [InteractionType.FEEDBACK]: {
      icon: MessageSquare,
      label: t('interactions.interactionTypeINeedYou'),
      colorClass: 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-50',
      iconColor: 'text-blue-600',
    },
    [InteractionType.DEBRIEF]: {
      icon: Users,
      label: t('interactions.interactionTypeILearn'),
      colorClass:
        'bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-50',
      iconColor: 'text-purple-600',
    },
  };

  const formatDate = (date: Date) => {
    const locale = i18n.language === 'he' ? 'he-IL' : 'en-US';
    return new Date(date).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatUserName = (user: {
    personalInfo: { firstName: string; lastName: string };
  }) => {
    return `${user.personalInfo.firstName} ${user.personalInfo.lastName}`;
  };

  const formatGoalName = (title?: string) => {
    return title ?? null;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg" className="max-h-[85vh]">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">
            {t('interactions.title')}
          </DialogTitle>
          <DialogDescription className="text-sm text-gray-600">
            {t('interactions.description')(goalTitle)}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 max-h-[60vh] overflow-y-auto pe-2">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="h-8 w-8 text-blue-500 animate-spin mb-3" />
              <p className="text-sm text-gray-600">
                {t('interactions.loading')}
              </p>
            </div>
          ) : interactions.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="p-4 bg-gray-50 rounded-full mb-3">
                <MessageSquare className="h-8 w-8 text-gray-400" />
              </div>
              <p className="text-sm text-gray-600">{t('interactions.empty')}</p>
            </div>
          ) : (
            interactions.map((interaction: PopulatedInteraction) => {
              const config = interactionConfig[interaction.type];
              const Icon = config.icon;

              if (
                includeInteractionTypes?.length &&
                !includeInteractionTypes?.includes(interaction.type)
              )
                return;

              return (
                <div
                  key={interaction._id}
                  className={cn(
                    'relative bg-white border rounded-xl p-5 shadow-sm',
                    'hover:shadow-md transition-all duration-200'
                  )}
                >
                  {/* Left Accent Bar */}
                  <div
                    className={cn(
                      'absolute left-0 top-0 bottom-0 w-1.5 rounded-l-xl',
                      interaction.type === InteractionType.PRAISE &&
                        'bg-gradient-to-b from-emerald-500 to-emerald-600',
                      interaction.type === InteractionType.FEEDBACK &&
                        'bg-gradient-to-b from-blue-500 to-blue-600',
                      interaction.type === InteractionType.DEBRIEF &&
                        'bg-gradient-to-b from-purple-500 to-purple-600'
                    )}
                  />

                  <div className="space-y-4">
                    {/* Header */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div
                          className={cn(
                            'p-2 rounded-lg shadow-sm',
                            interaction.type === InteractionType.PRAISE &&
                              'bg-gradient-to-br from-emerald-50 to-emerald-100/50',
                            interaction.type === InteractionType.FEEDBACK &&
                              'bg-gradient-to-br from-blue-50 to-blue-100/50',
                            interaction.type === InteractionType.DEBRIEF &&
                              'bg-gradient-to-br from-purple-50 to-purple-100/50'
                          )}
                        >
                          <Icon className={cn('h-5 w-5', config.iconColor)} />
                        </div>
                        <Badge
                          className={cn(
                            'border text-xs font-semibold px-3 py-1',
                            config.colorClass
                          )}
                        >
                          {config.label}
                        </Badge>
                        {/* Blockage Badge */}
                        {interaction.type === InteractionType.FEEDBACK &&
                          interaction.status !== FeedbackStatus.FINISHED && (
                            <Badge
                              className={cn(
                                'flex items-center gap-1.5 px-2.5 py-1',
                                'border text-xs font-semibold',
                                'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-50'
                              )}
                            >
                              <AlertTriangle className="h-3 w-3" />
                              {t('interactions.blockage')}
                            </Badge>
                          )}
                      </div>
                      <span className="text-xs text-gray-500 font-semibold bg-gray-50 px-3 py-1.5 rounded-full">
                        {formatDate(interaction.metadata.createdAt)}
                      </span>
                    </div>

                    {(interaction.goal?.title &&
                      formatGoalName(interaction.goal.title)) ||
                    interaction.type === InteractionType.FEEDBACK ? (
                      <div className="flex items-center justify-between gap-4 text-sm">
                        {interaction.goal?.title && (
                          <div className="flex items-center gap-2">
                            <span className="text-gray-500 font-medium">
                              {t('interactions.goalLabel')}
                            </span>
                            <span className="text-gray-700">
                              {formatGoalName(interaction.goal.title)}
                            </span>
                          </div>
                        )}
                        {interaction.type === InteractionType.FEEDBACK && (
                          <div className="flex items-center gap-2">
                            <span className="text-gray-500 font-medium">
                              {t('interactions.status')}:
                            </span>
                            <span className="text-gray-900 font-semibold">
                              {interaction.status}
                            </span>
                          </div>
                        )}
                      </div>
                    ) : null}

                    {/* Content */}
                    <div className="space-y-4">
                      {interaction.type === InteractionType.PRAISE && (
                        <div className="space-y-4">
                          {/* User Information */}
                          <div className="bg-gradient-to-br from-gray-50 to-white rounded-lg p-4 border border-gray-100">
                            <div className="grid grid-cols-2 gap-6">
                              {/* Sender */}
                              <div className="space-y-3">
                                <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">
                                  {t('interactions.sender')}
                                </p>
                                <div className="flex items-center gap-3">
                                  <UserSelectorAvatar
                                    fullName={formatUserName(
                                      interaction.creatorId
                                    )}
                                    imageUrl={
                                      interaction.creatorId.personalInfo
                                        .imageUrl
                                    }
                                    size="md"
                                  />
                                  <span className="text-sm text-gray-900 font-semibold">
                                    {formatUserName(interaction.creatorId)}
                                  </span>
                                </div>
                              </div>

                              {/* Receiver */}
                              <div className="space-y-3">
                                <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">
                                  {t('interactions.receiver')}
                                </p>
                                <div className="flex items-center gap-3">
                                  <UserSelectorAvatar
                                    fullName={formatUserName(
                                      interaction.receiverId
                                    )}
                                    imageUrl={
                                      interaction.receiverId.personalInfo
                                        .imageUrl
                                    }
                                    size="md"
                                  />
                                  <span className="text-sm text-gray-900 font-semibold">
                                    {formatUserName(interaction.receiverId)}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Company Values */}
                          {/* {interaction.companyValues.length > 0 && (
                            <div>
                              <p className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-2">
                                Company Values
                              </p>
                              <div className="flex flex-wrap gap-2">
                                {interaction.companyValues.map((cv, idx) => (
                                  <Badge
                                    key={idx}
                                    variant="outline"
                                    className="text-xs bg-emerald-50 border-emerald-200 text-emerald-700 font-medium"
                                  >
                                    {cv.value}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          )} */}
                        </div>
                      )}

                      {interaction.type === InteractionType.FEEDBACK && (
                        <div className="space-y-4">
                          {/* User Information */}
                          <div className="bg-gradient-to-br from-gray-50 to-white rounded-lg p-4 border border-gray-100">
                            <div className="grid grid-cols-2 gap-6">
                              {/* Sender */}
                              <div className="space-y-3">
                                <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">
                                  {t('interactions.sender')}
                                </p>
                                <div className="flex items-center gap-3">
                                  <UserSelectorAvatar
                                    fullName={formatUserName(
                                      interaction.creatorId
                                    )}
                                    imageUrl={
                                      interaction.creatorId.personalInfo
                                        .imageUrl
                                    }
                                    size="md"
                                  />
                                  <span className="text-sm text-gray-900 font-semibold">
                                    {formatUserName(interaction.creatorId)}
                                  </span>
                                </div>
                              </div>

                              {/* Receiver */}
                              <div className="space-y-3">
                                <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">
                                  {t('interactions.receiver')}
                                </p>
                                <div className="flex items-center gap-3">
                                  <UserSelectorAvatar
                                    fullName={formatUserName(
                                      interaction.receiverId
                                    )}
                                    imageUrl={
                                      interaction.receiverId.personalInfo
                                        .imageUrl
                                    }
                                    size="md"
                                  />
                                  <span className="text-sm text-gray-900 font-semibold">
                                    {formatUserName(interaction.receiverId)}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Company Values */}
                          {/* {interaction.companyValues.length > 0 && (
                            <div>
                              <p className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-2">
                                Company Values
                              </p>
                              <div className="flex flex-wrap gap-2">
                                {interaction.companyValues.map((cv, idx) => (
                                  <Badge
                                    key={idx}
                                    variant="outline"
                                    className="text-xs bg-blue-50 border-blue-200 text-blue-700 font-medium"
                                  >
                                    {cv.value} (Impact: {cv.impact})
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          )} */}
                        </div>
                      )}

                      {interaction.type === InteractionType.DEBRIEF && (
                        <div className="space-y-4">
                          <div className="bg-gradient-to-br from-purple-50 to-white rounded-lg p-4 border border-purple-100">
                            <div className="grid grid-cols-2 gap-6">
                              <div>
                                <p className="text-xs text-purple-600 font-medium uppercase tracking-wide mb-2">
                                  {t('interactions.coreParticipants')}
                                </p>
                                <p className="text-2xl font-bold text-purple-900">
                                  {interaction.coreParticipants.length}
                                </p>
                              </div>
                              <div>
                                <p className="text-xs text-purple-600 font-medium uppercase tracking-wide mb-2">
                                  {t('interactions.otherParticipants')}
                                </p>
                                <p className="text-2xl font-bold text-purple-900">
                                  {interaction.otherParticipants.length}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
